import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./styles/Partnerdetail.css";
import "./styles/Lenovo.css";
import lenovoLogo from "../../assets/images/partners/lenovo.png";

/* ── Animated Counter ── */
function useCounter(target, duration = 2000, inView) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration, inView]);
  return count;
}

function useInView(threshold = 0.3) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setInView(true); obs.disconnect(); }
    }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

/* ── Data ── */
const whyCards = [
  { icon: "🖥️", title: "Complete Technology Portfolio", body: "From endpoint devices to data center infrastructure, Lenovo's comprehensive portfolio covers every layer of your IT estate — reducing vendor sprawl and simplifying procurement and support." },
  { icon: "🔒", title: "Enterprise Security Built In", body: "ThinkShield delivers hardware-level security, TPM 2.0, self-healing BIOS, and software supply chain protection across every ThinkPad and ThinkCentre device from factory to retirement." },
  { icon: "📈", title: "Proven Scalability", body: "Lenovo's #1 global server market share and #1 PC market position reflect a platform enterprises trust to scale from 10 endpoints to 10,000 — with consistent quality across every SKU." },
  { icon: "🌱", title: "Sustainability Leadership", body: "Lenovo leads the industry in circular economy practices, recyclable packaging, and carbon-offset programs — helping organizations meet ESG and sustainability reporting commitments." },
  { icon: "🔧", title: "Premier Support Services", body: "Lenovo Premier Support delivers direct access to elite technicians with on-site response, accidental damage protection, and dedicated account management for enterprise customers." },
  { icon: "🤝", title: "ALG Data Guard Partnership", body: "As a Lenovo authorized partner, ALG Data Guard assists with hardware selection, configuration, deployment, asset management, and full lifecycle planning for your Lenovo estate." },
];

const products = [
  {
    name: "ThinkSystem Servers",
    tag: "Data Center",
    img: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=700&q=80",
    desc: "Enterprise server platforms delivering performance, reliability, and scalability for any workload. From edge computing to core data center and HPC deployments.",
    specs: ["Intel Xeon / AMD EPYC", "High-Density Compute", "NVMe Storage Ready", "GPU Expansion"],
  },
  {
    name: "ThinkPad Laptops",
    tag: "Endpoint Computing",
    img: "https://images.unsplash.com/photo-1496181133206-80ce9b88a853?auto=format&fit=crop&w=700&q=80",
    desc: "The world's most trusted business laptop. Engineered for durability, security, and productivity across hybrid and mobile work environments.",
    specs: ["MIL-SPEC Tested", "ThinkShield Security", "Long Battery Life", "5G Connectivity Ready"],
  },
  {
    name: "ThinkStation Workstations",
    tag: "High-Performance",
    img: "https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?auto=format&fit=crop&w=700&q=80",
    desc: "Certified professional workstations for design, engineering, and AI workflows. ISV-certified for leading software suites from Autodesk, Dassault, and Adobe.",
    specs: ["NVIDIA RTX / Quadro GPU", "ISV Certified", "ECC Memory Support", "PCIe Gen 5 Ready"],
  },
  {
    name: "ThinkCentre Desktops",
    tag: "Office Computing",
    img: "https://images.unsplash.com/photo-1587831990711-23ca6441447b?auto=format&fit=crop&w=700&q=80",
    desc: "Standardized desktop platforms for modern office environments. Tiny form factors to tower configs — all with centralized management and consistent security posture.",
    specs: ["vPro Ready", "Remote Management", "Ultra-Small Form Factor", "Carbon Neutral Options"],
  },
  {
    name: "ThinkSystem Storage",
    tag: "Storage Solutions",
    img: "https://images.unsplash.com/photo-1597852074816-d933c7d2b988?auto=format&fit=crop&w=700&q=80",
    desc: "Scalable storage platforms from all-flash arrays to hybrid storage — delivering the performance, capacity, and data protection modern enterprises demand.",
    specs: ["All-Flash Performance", "NVMe & SAS Support", "Data Deduplication", "Seamless Tiering"],
  },
  {
    name: "Lenovo TruScale",
    tag: "As-a-Service",
    img: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=700&q=80",
    desc: "Lenovo's as-a-service model delivers infrastructure on a consumption-based model — eliminating large upfront investments while ensuring access to the latest technology.",
    specs: ["Pay-Per-Use Model", "Elastic Capacity", "Refresh On Demand", "Managed Support"],
  },
];

const techFeatures = [
  {
    title: "ThinkSystem Servers for AI & Data Center",
    body: "Lenovo ThinkSystem SR and SD-series servers are engineered for modern AI workloads, high-density virtualization, and cloud-native applications. With support for Intel Xeon Scalable and AMD EPYC processors, PCIe Gen 5 expansion, and up to 8 NVIDIA GPUs per node, ThinkSystem is the foundation of modern enterprise AI infrastructure.",
    img: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=900&q=80",
    points: ["Dual Intel Xeon or AMD EPYC processor support", "Up to 8 x NVIDIA H100/A100 GPU slots", "NVMe-oF and direct-attach storage flexibility", "OpenManage integration for unified operations"],
    reverse: false,
  },
  {
    title: "ThinkPad — The World's Premier Business Laptop",
    body: "The ThinkPad is more than a laptop — it's an engineering philosophy. Every ThinkPad passes MIL-SPEC 810H durability testing across 12 military-grade standards. ThinkShield delivers hardware-rooted security from firmware to software, protecting sensitive enterprise data at every layer.",
    img: "https://images.unsplash.com/photo-1496181133206-80ce9b88a853?auto=format&fit=crop&w=900&q=80",
    points: ["MIL-SPEC 810H — 12 military durability standards", "ThinkShield hardware security from BIOS level", "Intel vPro for enterprise remote management", "Up to 32 hours battery on select models"],
    reverse: true,
  },
  {
    title: "AI-Ready Infrastructure with Lenovo Neptune Cooling",
    body: "Lenovo's direct water cooling (Neptune™ technology) enables high-density AI and HPC infrastructure that traditional air cooling cannot support. Deploy GPU-dense AI training infrastructure with significantly lower power consumption and reduced cooling infrastructure costs.",
    img: "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=900&q=80",
    points: ["Direct water cooling (Neptune) for GPU-dense clusters", "Up to 40% lower cooling energy consumption", "AI Factory reference architecture", "Validated for NVIDIA DGX and HGX platforms"],
    reverse: false,
  },
];

const industries = [
  { icon: "🏥", name: "Healthcare", desc: "HIPAA-ready endpoint devices for clinical use, diagnostic workstations, and data center infrastructure for healthcare systems." },
  { icon: "🏦", name: "Financial Services", desc: "High-availability compute for trading, banking applications, and compliant endpoint management at enterprise scale." },
  { icon: "🎓", name: "Education", desc: "Scalable student devices, teaching workstations, and campus data center infrastructure for modern educational institutions." },
  { icon: "🏛️", name: "Government", desc: "FIPS-validated, Common Criteria-certified endpoint platforms meeting federal security requirements and procurement standards." },
  { icon: "🏭", name: "Manufacturing", desc: "Rugged workstations for factory floors, edge server platforms for OT environments, and smart manufacturing analytics." },
  { icon: "🛒", name: "Retail", desc: "Compact point-of-sale and back-office computing platforms with centralized management across distributed store locations." },
  { icon: "📡", name: "Telecommunications", desc: "High-density server and storage infrastructure for telecom workloads, 5G core deployments, and OSS/BSS modernization." },
  { icon: "⚡", name: "Energy & Utilities", desc: "Ruggedized compute for field operations, edge analytics for grid management, and HPC for energy simulation workloads." },
];

const lifecycleSteps = [
  { num: "01", icon: "🔍", title: "Assessment", desc: "Evaluate your current hardware estate, identify refresh priorities, and model total cost of ownership for the Lenovo transition." },
  { num: "02", icon: "📐", title: "Design", desc: "Select the right Lenovo configurations for your users, workloads, and data center requirements with expert guidance from ALG Data Guard." },
  { num: "03", icon: "📦", title: "Procurement", desc: "Simplify purchasing through ALG Data Guard as your Lenovo authorized reseller — competitive pricing, consolidated ordering, and flexible payment options." },
  { num: "04", icon: "🚀", title: "Deployment", desc: "Pre-configured, image-ready devices and server infrastructure deployed to specification — minimizing IT staff burden and deployment time." },
  { num: "05", icon: "⚙️", title: "Management", desc: "Ongoing device and infrastructure management including patching, monitoring, asset tracking, and configuration compliance." },
  { num: "06", icon: "♻️", title: "Refresh", desc: "Structured device lifecycle planning with secure data destruction, trade-in programs, and seamless next-generation transitions." },
];

const benefits = [
  { icon: "💰", title: "Reduced TCO Through Standardization", desc: "Standardizing on Lenovo across endpoints and data center simplifies procurement, reduces support costs, and improves lifecycle predictability." },
  { icon: "🛡️", title: "Zero-Trust Hardware Security", desc: "ThinkShield's layered hardware-to-cloud security approach eliminates firmware vulnerabilities and protects against advanced persistent threats at the silicon level." },
  { icon: "⚡", title: "AI-Ready Infrastructure Today", desc: "Lenovo's validated AI reference architectures and Neptune-cooled GPU platforms accelerate AI adoption without requiring infrastructure reinvention." },
  { icon: "🌐", title: "Global Support Coverage", desc: "Lenovo's Premier Support network provides next-business-day on-site service in 160+ countries — ensuring uptime for globally distributed operations." },
];

const certifications = [
  { badge: "🏆", name: "Lenovo Gold Partner", desc: "Gold-tier Lenovo authorized reseller and deployment partner for commercial and enterprise products" },
  { badge: "⭐", name: "ThinkPad Expert Certification", desc: "Certified ThinkPad sales and technical expertise for enterprise endpoint deployments" },
  { badge: "🖥️", name: "ThinkSystem Infrastructure", desc: "Authorized partner for Lenovo data center server, storage, and networking products" },
  { badge: "🔧", name: "Premier Support Partner", desc: "Authorized to deliver Lenovo Premier Support services for enterprise maintenance contracts" },
];

const faqData = [
  { q: "What is Lenovo's ThinkShield and why does it matter for enterprise security?", a: "ThinkShield is Lenovo's comprehensive hardware-to-cloud security framework covering the entire device lifecycle. It includes Self-Healing BIOS (the industry's first) that can recover from firmware attacks, hardware TPM 2.0, FIDO2 authentication, software supply chain transparency, and matched-pairs protection that permanently links hardware to authorized firmware. For enterprises managing sensitive data, ThinkShield provides a hardware-rooted trust foundation that software-only security cannot replicate." },
  { q: "How does Lenovo TruScale differ from traditional hardware purchasing?", a: "TruScale is Lenovo's Infrastructure-as-a-Service model that delivers hardware on a consumption or subscription basis. Instead of capital expenditure on hardware, organizations pay monthly based on actual usage — with Lenovo retaining ownership and managing refresh cycles. TruScale eliminates large upfront purchases, provides flexibility to scale capacity, and ensures access to current-generation technology without the burden of residual value risk." },
  { q: "What industries are Lenovo devices certified for?", a: "Lenovo devices carry certifications for Healthcare (HHS HIPAA-aligned), Government (FIPS 140-2, Common Criteria, GSA Schedule), Military (MIL-SPEC 810H for ThinkPad), and professional creative/engineering workflows (ISV certifications from Autodesk, Dassault Systèmes, Adobe, and ANSYS for ThinkStation). ALG Data Guard can help identify the right Lenovo SKUs for your compliance and certification requirements." },
  { q: "Can ALG Data Guard provide Lenovo devices pre-configured with our OS and software image?", a: "Yes. As a Lenovo authorized partner, ALG Data Guard offers image loading and custom configuration services so devices arrive deployment-ready with your corporate OS image, applications, and security configuration applied at the factory level through Lenovo's Smart Deployment service — dramatically reducing IT burden and deployment time for large device rollouts." },
  { q: "What is Lenovo's approach to AI and high-performance computing?", a: "Lenovo is the world's #1 HPC provider (Top500 list) and has invested heavily in AI infrastructure with its AI Factory reference architectures. ThinkSystem servers support NVIDIA DGX, HGX, and PCIe GPU configurations with Neptune direct water cooling enabling extreme density impossible with air cooling. Lenovo's validated AI reference designs span training clusters, inference infrastructure, and edge AI platforms." },
  { q: "How does Lenovo support device lifecycle management at enterprise scale?", a: "Lenovo's Device Intelligence platform provides automated device health monitoring, predictive failure analytics, and proactive support alerts. Combined with Lenovo's Unified Endpoint Management integrations (Microsoft Intune, SCCM, JAMF), enterprises gain full lifecycle visibility — from deployment through retirement — with automated workflows for procurement, configuration, maintenance, and secure disposal." },
];

const ArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M13 5l7 7-7 7" />
  </svg>
);
const ChevronDown = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 9l6 6 6-6" />
  </svg>
);

export default function Lenovo() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [metricsRef, metricsInView] = useInView(0.3);

  const c1 = useCounter(750, 2200, metricsInView);
  const c2 = useCounter(98, 2000, metricsInView);
  const c3 = useCounter(180, 2000, metricsInView);
  const c4 = useCounter(15, 2000, metricsInView);

  return (
    <div className="lenovo-v2-page pd-page">
      {/* ── Identity Band ── */}
      <div className="pd-identity-band">
        <div className="pd-identity-inner">
          <img src={lenovoLogo} alt="Lenovo" className="pd-partner-logo" />
          <div className="pd-identity-divider" aria-hidden="true" />
          <div className="pd-identity-text">
            <div className="pd-eyebrow pd-eyebrow--dark">
              <span className="pd-live-dot pd-live-dot--dark" />
              Enterprise Devices & Infrastructure
            </div>
            <h1 className="pd-identity-name">ALG Data Guard × Lenovo</h1>
            <p className="pd-identity-desc">
              Lenovo — the world's #1 PC vendor and a global leader in data center infrastructure —
              delivers enterprise-grade hardware trusted by 180 countries. ALG Data Guard is your
              authorized Lenovo partner for endpoint computing, data center servers, AI infrastructure,
              and complete device lifecycle management.
            </p>
          </div>
        </div>
      </div>

      {/* ── Hero ── */}
      <section className="lv-hero">
        <div className="lv-hero__bg-lines" aria-hidden="true" />
        <div className="lv-hero__glow" aria-hidden="true" />
        <div className="lv-hero__inner">
          <div className="lv-hero__content">
            <div className="lv-hero__kicker">
              <span className="lv-hero__kicker-dot" />
              Lenovo Gold Partner — Authorized Reseller
            </div>
            <h2 className="lv-hero__title">
              Smarter Devices. Stronger <em>Infrastructure</em>. Better Business.
            </h2>
            <p className="lv-hero__body">
              From the ThinkPad on every executive's desk to the ThinkSystem servers powering
              your data center, Lenovo's complete technology portfolio helps organizations
              modernize endpoints, accelerate AI workloads, and build resilient infrastructure —
              all with a single trusted partner: ALG Data Guard.
            </p>
            <div className="lv-hero__actions">
              <button className="lv-btn-primary" onClick={() => navigate("/contact")}>
                Request a Lenovo Consultation <ArrowIcon />
              </button>
              <button className="lv-btn-outline" onClick={() => navigate("/contact")}>
                Explore Solutions
              </button>
            </div>
            <div className="lv-hero__stats">
              <div>
                <div className="lv-hero__stat-value">#1</div>
                <div className="lv-hero__stat-label">Global PC Market Share</div>
              </div>
              <div>
                <div className="lv-hero__stat-value">#1</div>
                <div className="lv-hero__stat-label">Global HPC Provider</div>
              </div>
              <div>
                <div className="lv-hero__stat-value">180+</div>
                <div className="lv-hero__stat-label">Countries Served</div>
              </div>
            </div>
          </div>
          <div className="lv-hero__device-card">
            <img
              src="https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?auto=format&fit=crop&w=700&q=80"
              alt="Lenovo ThinkPad enterprise device"
              className="lv-hero__device-img"
            />
            <div className="lv-hero__device-info">
              {[
                { val: "MIL-SPEC", label: "810H Certified" },
                { val: "32hr", label: "Battery Life" },
                { val: "vPro", label: "Remote Manage" },
                { val: "FIPS", label: "Security Ready" },
              ].map((spec) => (
                <div key={spec.val} className="lv-hero__device-spec">
                  <div className="lv-hero__device-spec-val">{spec.val}</div>
                  <div className="lv-hero__device-spec-label">{spec.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Why Lenovo ── */}
      <section className="lv-why">
        <div className="lv-shell">
          <div className="lv-why__head">
            <span className="lv-badge"><span className="lv-dot" /> Why Lenovo</span>
            <h2 className="lv-section-title">Technology Built for Enterprise Performance</h2>
            <p className="lv-section-sub">
              Lenovo's #1 global market position in PCs and HPC isn't an accident — it reflects decades
              of engineering discipline, security innovation, and enterprise reliability that organizations
              in 180 countries depend on every day.
            </p>
          </div>
          <div className="lv-why__grid">
            {whyCards.map((card) => (
              <div key={card.title} className="lv-why-card">
                <div className="lv-why-card__icon">{card.icon}</div>
                <h3 className="lv-why-card__title">{card.title}</h3>
                <p className="lv-why-card__body">{card.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Product Categories ── */}
      <section className="lv-products">
        <div className="lv-shell">
          <div className="lv-products__head">
            <span className="lv-badge" style={{ background: "rgba(226,35,26,0.2)", borderColor: "rgba(226,35,26,0.4)", color: "#ff7777" }}>
              <span className="lv-dot" /> Product Portfolio
            </span>
            <h2 className="lv-section-title">Lenovo Products Across Your Entire IT Estate</h2>
            <p className="lv-section-sub">
              From endpoints to edge to enterprise data center, Lenovo delivers the hardware
              your organization needs to perform, scale, and secure every layer of IT.
            </p>
          </div>
          <div className="lv-products__grid">
            {products.map((p) => (
              <div key={p.name} className="lv-product-card">
                <div className="lv-product-card__img-wrap">
                  <img src={p.img} alt={p.name} className="lv-product-card__img" />
                  <span className="lv-product-card__tag">{p.tag}</span>
                </div>
                <div className="lv-product-card__body">
                  <h3 className="lv-product-card__name">{p.name}</h3>
                  <p className="lv-product-card__desc">{p.desc}</p>
                  <div className="lv-product-card__specs">
                    {p.specs.map((s) => (
                      <span key={s} className="lv-product-card__spec">{s}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Featured Technologies ── */}
      <section className="lv-tech">
        <div className="lv-shell">
          <div className="lv-tech__head">
            <span className="lv-badge"><span className="lv-dot" /> Featured Technologies</span>
            <h2 className="lv-section-title">Lenovo's Flagship Enterprise Platforms</h2>
            <p className="lv-section-sub">
              A deep look at the Lenovo technologies that define modern enterprise computing —
              from the boardroom to the data center to the AI lab.
            </p>
          </div>
          {techFeatures.map((feat) => (
            <div key={feat.title} className={`lv-tech-feature${feat.reverse ? " lv-tech-feature--reverse" : ""}`}>
              <img src={feat.img} alt={feat.title} className="lv-tech-feature__img" />
              <div>
                <span className="lv-badge"><span className="lv-dot" /> Lenovo Platform</span>
                <h3 className="lv-tech-feature__title">{feat.title}</h3>
                <p className="lv-tech-feature__body">{feat.body}</p>
                <ul className="lv-tech-feature__points">
                  {feat.points.map((pt) => <li key={pt}>{pt}</li>)}
                </ul>
                <button className="lv-btn-primary" onClick={() => navigate("/contact")}>
                  Learn More <ArrowIcon />
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Industries ── */}
      <section className="lv-industries">
        <div className="lv-shell">
          <div className="lv-industries__head">
            <span className="lv-badge" style={{ background: "rgba(226,35,26,0.2)", borderColor: "rgba(226,35,26,0.4)", color: "#ff7777" }}>
              <span className="lv-dot" /> Industries We Serve
            </span>
            <h2 className="lv-section-title">Lenovo Solutions Across Every Vertical</h2>
            <p className="lv-section-sub">
              Lenovo's industry-validated hardware platforms meet the unique requirements
              of regulated, high-performance, and mission-critical environments across every sector.
            </p>
          </div>
          <div className="lv-industries__grid">
            {industries.map((ind) => (
              <div key={ind.name} className="lv-industry-card">
                <span className="lv-industry-card__icon">{ind.icon}</span>
                <h3 className="lv-industry-card__name">{ind.name}</h3>
                <p className="lv-industry-card__desc">{ind.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Lifecycle ── */}
      <section className="lv-lifecycle">
        <div className="lv-shell">
          <div className="lv-lifecycle__head">
            <span className="lv-badge"><span className="lv-dot" /> Delivery Methodology</span>
            <h2 className="lv-section-title">End-to-End Lenovo Lifecycle Management</h2>
            <p className="lv-section-sub">
              ALG Data Guard manages every stage of your Lenovo hardware lifecycle —
              from procurement and deployment through management, support, and secure retirement.
            </p>
          </div>
          <div className="lv-lifecycle__steps">
            {lifecycleSteps.map((step) => (
              <div key={step.num} className="lv-lifecycle-step">
                <div className="lv-lifecycle-step__num">{step.num}</div>
                <span className="lv-lifecycle-step__icon">{step.icon}</span>
                <h3 className="lv-lifecycle-step__title">{step.title}</h3>
                <p className="lv-lifecycle-step__desc">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Metrics ── */}
      <section className="lv-metrics" ref={metricsRef}>
        <div className="lv-shell">
          <div className="lv-metrics__inner">
            <div>
              <div className="lv-metric__value">{c1}+</div>
              <div className="lv-metric__label">Devices Deployed</div>
            </div>
            <div>
              <div className="lv-metric__value">{c2}%</div>
              <div className="lv-metric__label">Customer Satisfaction</div>
            </div>
            <div>
              <div className="lv-metric__value">{c3}+</div>
              <div className="lv-metric__label">Enterprise Clients</div>
            </div>
            <div>
              <div className="lv-metric__value">{c4}+</div>
              <div className="lv-metric__label">Years of Lenovo Expertise</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Benefits ── */}
      <section className="lv-benefits">
        <div className="lv-shell">
          <div className="lv-benefits__inner">
            <div>
              <img
                src="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=800&q=80"
                alt="Enterprise technology team"
                className="lv-benefits__img"
              />
            </div>
            <div className="lv-benefits__content">
              <span className="lv-badge" style={{ background: "rgba(226,35,26,0.2)", borderColor: "rgba(226,35,26,0.4)", color: "#ff7777" }}>
                <span className="lv-dot" /> Business Benefits
              </span>
              <h2 className="lv-section-title">The Value of the Right Hardware Partner</h2>
              <p className="lv-section-sub">
                Choosing Lenovo and ALG Data Guard means more than buying hardware —
                it means investing in a technology partnership that delivers measurable business outcomes.
              </p>
              <div className="lv-benefits__list">
                {benefits.map((b) => (
                  <div key={b.title} className="lv-benefit-item">
                    <div className="lv-benefit-item__icon">{b.icon}</div>
                    <div>
                      <div className="lv-benefit-item__title">{b.title}</div>
                      <p className="lv-benefit-item__desc">{b.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Certifications ── */}
      <section className="lv-certs">
        <div className="lv-shell">
          <div className="lv-certs__head">
            <span className="lv-badge"><span className="lv-dot" /> Our Lenovo Credentials</span>
            <h2 className="lv-section-title">Authorized Lenovo Partner</h2>
            <p className="lv-section-sub">
              ALG Data Guard holds Lenovo partner certifications that validate our ability to
              advise, procure, deploy, and support Lenovo technology across your entire estate.
            </p>
          </div>
          <div className="lv-certs__grid">
            {certifications.map((c) => (
              <div key={c.name} className="lv-cert-card">
                <div className="lv-cert-card__badge">{c.badge}</div>
                <div className="lv-cert-card__name">{c.name}</div>
                <div className="lv-cert-card__desc">{c.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="lv-faq">
        <div className="lv-shell">
          <div className="lv-faq__head">
            <span className="lv-badge" style={{ background: "rgba(226,35,26,0.2)", borderColor: "rgba(226,35,26,0.4)", color: "#ff7777" }}>
              <span className="lv-dot" /> Frequently Asked Questions
            </span>
            <h2 className="lv-section-title">Lenovo Questions, Answered</h2>
            <p className="lv-section-sub">
              Common questions about Lenovo products, licensing, and how ALG Data Guard delivers them.
            </p>
          </div>
          <div className="lv-faq__list">
            {faqData.map((faq, i) => (
              <div key={i} className={`lv-faq-item${openFaq === i ? " open" : ""}`}>
                <button className="lv-faq-item__trigger" onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i}>
                  <span className="lv-faq-item__q">{faq.q}</span>
                  <span className="lv-faq-item__arrow"><ChevronDown /></span>
                </button>
                <div className="lv-faq-item__answer">
                  <div className="lv-faq-item__answer-inner">{faq.a}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="lv-cta">
        <div className="lv-cta__inner">
          <span className="lv-badge" style={{ background: "rgba(226,35,26,0.25)", borderColor: "rgba(226,35,26,0.5)", color: "#ff9999", margin: "0 auto 1.2rem" }}>
            <span className="lv-dot" /> Get Started
          </span>
          <h2 className="lv-cta__title">
            Modernize Your Devices and Infrastructure with Lenovo and ALG Data Guard
          </h2>
          <p className="lv-cta__sub">
            Whether you're planning an endpoint refresh, data center expansion, or AI infrastructure project,
            ALG Data Guard is your authorized Lenovo partner for expert guidance, competitive pricing, and
            end-to-end deployment support.
          </p>
          <div className="lv-cta__actions">
            <button className="lv-btn-primary" onClick={() => navigate("/contact")}>
              Request a Lenovo Consultation <ArrowIcon />
            </button>
            <button className="lv-btn-outline" onClick={() => navigate("/contact")}>
              Get a Quote
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}